import { Link } from "react-router-dom";
import { Briefcase, Clapperboard, ShoppingBag, Trophy, ArrowRight, Headset } from "lucide-react";

const FOREST_BG = "https://static.prod-images.emergentagent.com/jobs/1c220779-6edd-4bd3-b2c7-23a9d0486762/images/30772e068c67c998a3f9d7e1635210799fdcb80e5fd48302cae8ec811f38af6c.jpeg";

const SECTIONS = [
  {
    id: "business",
    to: "/business",
    icon: Briefcase,
    title: "BUSINESS",
    desc: "Rent futuristic meeting suites in the canopy — by the hour, paid in USDT.",
    items: ["Meeting room rental", "4 to 40 seats", "Hourly booking"],
    accent: "#2EE38A",
  },
  {
    id: "entertainment",
    to: "/welcome",
    icon: Clapperboard,
    title: "ENTERTAINMENT",
    desc: "Crypto games, the forest cinema and the VR plaza — all in one district.",
    items: ["Crash · Limbo · Tower", "Cinema tickets", "VR Plaza (headset ready)"],
    accent: "#A8F0CE",
  },
  {
    id: "shopping",
    to: "/shopping",
    icon: ShoppingBag,
    title: "SHOPPING",
    desc: "GreenMart groceries, TechNova electronics and the character market.",
    items: ["GreenMart · fresh & grocery", "TechNova · gadgets", "Character market"],
    accent: "#FFA149",
  },
  {
    id: "sports",
    to: "/sports",
    icon: Trophy,
    title: "SPORTS",
    desc: "Live football scores, boxing arena, fitness memberships and pro coaches.",
    items: ["Live match center", "Boxing arena", "Fitness & coach hire"],
    accent: "#8FE3A8",
  },
];

const FIREFLIES = [
  { left: "8%", top: "22%", delay: "0s" }, { left: "18%", top: "64%", delay: "1.4s" },
  { left: "31%", top: "38%", delay: "2.8s" }, { left: "44%", top: "72%", delay: "0.8s" },
  { left: "55%", top: "28%", delay: "3.4s" }, { left: "66%", top: "58%", delay: "1.9s" },
  { left: "76%", top: "34%", delay: "4.2s" }, { left: "86%", top: "66%", delay: "0.4s" },
  { left: "92%", top: "24%", delay: "2.2s" }, { left: "38%", top: "16%", delay: "5s" },
];

export default function Ecosystem() {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden" data-testid="ecosystem-page">
      <div className="absolute inset-0">
        <img src={FOREST_BG} alt="" className="w-full h-full object-cover eco-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-800/25 via-ink-800/10 to-ink-800/75" />
        {FIREFLIES.map((f, i) => (
          <span key={i} className="firefly" style={{ left: f.left, top: f.top, animationDelay: f.delay }} />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-16">
        <div className="max-w-2xl">
          <div className="label-mono text-brand-magenta mb-3">// one_ecosystem_everything_usdt</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none" data-testid="ecosystem-title">
            METAVERSE
            <br />
            <span className="text-brand-magenta">ECOSYSTEM</span>.
          </h1>
          <p className="text-white/80 font-body mt-4 text-base md:text-lg">
            Work, play, shop and compete inside one living digital forest. Every rental, ticket, product and membership — paid with USDT.
          </p>
          <Link
            to="/plaza"
            className="inline-flex items-center gap-3 mt-6 px-6 py-3.5 bg-brand-magenta text-ink-900 font-display font-black text-sm uppercase tracking-widest hover:brightness-110 transition-all shadow-glow-magenta"
            data-testid="enter-3d-world-button"
          >
            <Headset className="w-5 h-5" /> ENTER 3D WORLD · VR READY
          </Link>
          <div className="label-mono text-white/50 mt-2">works in browser — put on your VR headset and press Enter VR</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.id}
                to={s.to}
                className="group relative bg-ink-800/55 backdrop-blur-md border border-white/20 hover:border-white/40 p-6 lg:p-8 transition-all hover:-translate-y-1"
                style={{ animationDelay: `${i * 90}ms` }}
                data-testid={`eco-section-${s.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 border-2 grid place-items-center" style={{ borderColor: s.accent, boxShadow: `0 0 22px ${s.accent}33` }}>
                    <Icon className="w-7 h-7" style={{ color: s.accent }} strokeWidth={2} />
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </div>
                <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tighter mt-5" style={{ color: s.accent }}>
                  {s.title}
                </h2>
                <p className="text-white/60 font-body text-sm mt-2">{s.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.items.map((it) => (
                    <li key={it} className="text-xs font-mono text-white/45 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full" style={{ background: s.accent }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap gap-3 items-center">
          <span className="label-mono text-white/40">quick access:</span>
          {[
            { label: "Wallet", to: "/wallet" },
            { label: "Crews", to: "/crews" },
            { label: "Passes", to: "/membership" },
            { label: "Leaderboard", to: "/leaderboard" },
          ].map((l) => (
            <Link key={l.to} to={l.to} className="px-3 py-1.5 text-xs font-mono bg-black/40 border border-white/15 text-white/70 hover:text-white hover:border-brand-magenta transition-colors backdrop-blur" data-testid={`eco-quick-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
